/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react'
import {
  EditableComponent,
  ModelProps
} from '@adobe/aem-react-editable-components'
import {
  ConfigureAndBuy as CAndB,
  ColorSelectorTypes,
  StockStatuses,
  MiniCart,
  AddToCart,
  ThemeUtils,
  DialogMaxWidthSizes,
  IconButtonStyles,
  Dialog as UIFoundationsDialog
} from '@dcxwam/dcx-wam-ui-foundations'
import {
  AttributeSelectorItemProps,
  AttributeSelectorProps
} from '@dcxwam/dcx-wam-ui-foundations/build/types/components/organisms/product/common/attribute-selector'
import { FlavourProfilesProps } from '@dcxwam/dcx-wam-ui-foundations/build/types/components/organisms/product/common/flavour-profiles'
import { ModelManager } from '@adobe/aem-spa-page-model-manager'
import { useCookies } from 'react-cookie'
import { canUseDom, useIsSsr } from '../../../utils/isSsr'
import { useUserData, UserTypes } from '../../../services/user/user-data'
import { useLocalStorageCart } from '../../../services/product/use-cart-local-storage'
import { AemConfigProxy } from '../../functional/AemConfigProxy/AemConfigProxy'
import {
  DEFAULT_IMAGE_WIDTH,
  GIGYA_COOKIE_JWT,
  GIGYA_COOKIE_UID,
  HYBRIS_ACCESS_TOKEN,
  IMAGE_GALLERY_BREAKPOINTS
} from '../../../utils/constants'
import ExperienceFragmentContent from '../../templates/ExperienceFragmentContent/ExperienceFragmentContent'
import { formatPrice } from '../../../utils/format-price'
import {
  setLayoutBottomShift,
  useInitLayoutTopShift
} from '../../../utils/app-layout'
import { setBreadcrumbTitle } from '../../../utils/breadcrumb-context'
import { handleAddToCart } from '../common/cart-management/handleAddToCart'
import CartErrorMessageDialog from '../common/cart-management/CartErrorMessageDialog'
import { createDataLayerEcommerceProduct } from '../common/datalayer/createDataLayerEcommerceProduct'
import { pushDataLayerProductDetailsView } from './datalayer'
import { getOptimizedImageExtension } from '../../../utils/image-extensions'
import {
  EditableComponentPropsType,
  GetHeadDataReturnType
} from '../../../utils/prop-types'
import { ProductStockData } from '../../../services/stock'
import {
  AttributeSelectorMarkers,
  ConfigureAndBuyModelProps,
  ConfigureAndBuyStaticModelProps,
  ProductProps,
  ProductVariantTypes
} from './types'
import withComponentRefresh from '../../../utils/aem/editor/withComponentRefresh'
import { checkIsPreorder } from '../../../utils/preorder'
import { getOrderQuantityValues } from '../../../utils/quantity-selector'
import headTagsConfig from './headTagsConfig'
import { getIntensityGauge } from '../common/intensity-gauge'
import { fetchProductData, fetchStockData } from './utils/productService'
import {
  createAddToCartMethodConfig,
  getAddToCartButtonLabel,
  getStockInfoLabel,
  useMiniCartConfig
} from './utils/cartConfig'
import { setMarkerProductData } from '../../../utils/marker-product-data'
import Container from '../../templates/Container/Container'
// eslint-disable-next-line @typescript-eslint/no-unused-expressions
canUseDom && import('@dcxwam/dcx-wam-ui-foundations/css')

const CURRENT_PRICE_PLACEHOLDER = '$CURRENT_PRICE'
const OLD_PRICE_PLACEHOLDER = '$OLD_PRICE'
const COLOR_SELECTOR_CONTAINER_ID = 'color-selector-container'

const stockStatusMap = {
  inStock: StockStatuses.InStock,
  outOfStock: StockStatuses.OutOfStock,
  lowStock: StockStatuses.LowStock
}

const outOfStockValue = 'outOfStock'

const deliveryDays = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday'
]

const splitProductCode = (productCode: string) => productCode.split('.')[0]

const getSortedVariants = (variants?: ProductVariantTypes[]) =>
  variants?.sort(
    (
      variantA: { sequenceNumber?: number; globalArticleCode: string },
      variantB: { sequenceNumber?: number; globalArticleCode: string }
    ) => {
      if (
        variantA?.sequenceNumber &&
        variantB?.sequenceNumber &&
        variantA?.sequenceNumber !== variantB?.sequenceNumber
      ) {
        return variantA?.sequenceNumber < variantB?.sequenceNumber ? -1 : 1
      }
      return variantA?.globalArticleCode < variantB?.globalArticleCode ? -1 : 1
    }
  ) || []

const ConfigureAndBuy: React.FC<
  EditableComponentPropsType<ConfigureAndBuyModelProps>
> &
  ConfigureAndBuyStaticModelProps = withComponentRefresh(({ ...props }) => {
  const {
    attributeSelector = [],
    id,
    key,
    config,
    ratesAndReviews,
    general,
    deliveryMessage,
    product,
    notifyMe,
    imageGallery,
    initialProductData,
    miniCart,
    addToCart,
    productSeoNames,
    flavour,
    productRootPath = '',
    ...rest
  } = props.model
  const editableProps = { ...props, model: { ...rest } }
  const isSsr = useIsSsr()
  const isMobile = ThemeUtils.isMobile()
  const pageLocale = ModelManager.modelStore.getData()?.config?.locale
  const attSelectorMarkers = config?.attributeSelectorMarkers ?? []
  // use current user hook before submiting add to cart request
  const userData = useUserData()
  // @ts-expect-error ConfigObjects are not defined
  const caConfig = AemConfigProxy?.getConfig(AemConfigProxy?.ConfigObjects)

  const { quantityLabel } = product as ProductProps

  const ERROR_MESSAGE_FROM_CA = product?.addToCartErrorMessage
  const topShift = useInitLayoutTopShift()
  const [cartLocalStorageJSON, setCartLocalStorage] = useLocalStorageCart()
  const productEntry = cartLocalStorageJSON?.entry
  const [isButtonDisabled, setIsButtonDisabled] = useState(isSsr)
  const [isButtonLoading, setIsButtonLoading] = useState(false)
  const [activeMiniCart, setActiveMiniCart] = useState(false)
  const [productData, setProductData] = useState(initialProductData)
  // set initial productID
  const [productID, setProductID] = useState(productData?.globalArticleCode)
  const visibleVariants = productData?.variants?.filter(
    (variant: ProductVariantTypes) =>
      productSeoNames?.includes(variant?.seo?.productsSeoUrl)
  )
  const [cookies, setCookie] = useCookies([
    GIGYA_COOKIE_UID,
    HYBRIS_ACCESS_TOKEN,
    GIGYA_COOKIE_JWT
  ])
  const [dialogErrorMessageIsOpen, setDialogErrorMessageIsOpen] =
    useState(false)
  const [dialogNotifyMeIsOpen, setDialogNotifyMeIsOpen] = useState(false)
  const [errorMessageContent, setErroMessageContent] = useState({
    message: null,
    title: ERROR_MESSAGE_FROM_CA,
    actions: null
  })
  const productsMapByGCode: Map<string, any> = new Map(
    productData?.variants?.map((variant) => [
      variant.globalArticleCode,
      variant
    ])
  )

  const [currentProductStockMap, setCurrentProductStockMap] = useState<
    Map<string, ProductStockData>
  >(
    new Map(
      productData?.variants?.map((productStock) => [
        productStock.globalArticleCode,
        productStock
      ])
    )
  )

  const fetchSData = async (productsMap) => {
    const cbxConfig = {
      cbxUrl: config?.cbxUrl,
      market: config?.market,
      channel: config?.channel
    }
    const stockData = await fetchStockData(
      cbxConfig,
      Array.from(productsMap.keys())
    )
    if (stockData?.length) {
      const productStockMap = new Map(
        stockData?.map((productStock) => [productStock.code, productStock])
      )
      setCurrentProductStockMap(productStockMap)
    }
  }

  const [variants, setVariants] = useState(getSortedVariants(visibleVariants))
  const currentProductVariant = {
    productFamily: productData?.productFamily,
    ...(variants?.find((v: any) => productID === v.globalArticleCode) || {})
  }
  const currentColorCode = splitProductCode(
    currentProductVariant?.pmiInternalProductCode || ''
  )
  const attributeSelectorMap = attributeSelector?.reduce(
    (acc: any, attr: any) => ({ ...acc, [attr.name]: attr.label }),
    {}
  )
  const [currentAttributeSelectedValues, setCurrentAttributeSelectedValues] =
    useState(
      attributeSelector?.reduce(
        (acc: any, attr: any) => ({
          ...acc,
          // @ts-expect-error Element implicitly has an 'any' type
          [attr.name]: currentProductVariant[attr.name]
        }),
        {}
      )
    )
  const mobileStickyCtaId = `${id}-sticky-cta`

  const attributeNames = Object.keys(attributeSelectorMap)
  const attributeNameOptions =
    variants?.reduce((acc: any, variant: any) => {
      const newAttributesAcc = { ...acc }
      attributeNames.forEach((attributeName) => {
        const options = acc[attributeName] || []
        if (!options.includes(variant[attributeName])) {
          options.push(variant[attributeName])
        }
        newAttributesAcc[attributeName] = options
      })
      return newAttributesAcc
    }, {}) || {}

  const findProduct = (
    selectedColorValue: string | undefined,
    selectedAttributes: Record<string, string>
  ) => {
    const currentColorVariant = variants?.find(
      (variant: any) =>
        splitProductCode(variant.pmiInternalProductCode) ===
          selectedColorValue &&
        attributeNames?.every(
          (attrName) => variant[attrName] === selectedAttributes[attrName]
        )
    )

    if (attributeNames?.length && !currentColorVariant) {
      return variants?.find(
        (variant: any) =>
          splitProductCode(variant.pmiInternalProductCode) ===
          selectedColorValue
      )
    }
    return currentColorVariant
  }

  const updateGcodeSetupSubscribers = () => {
    Object.values(ConfigureAndBuy.gcodeSetupSubscribers)?.forEach(
      (subscriber) => {
        subscriber?.action?.(key)
      }
    )
  }

  const onVariantChange = (clickedVariant: {
    globalArticleCode: string
    seo: { productsSeoUrl: string }
    nuance: { code: string }
  }) => {
    const queryString = window.location.search
    window.history.replaceState(
      null,
      '',
      encodeURIComponent(`${clickedVariant?.seo?.productsSeoUrl}.html`) +
        queryString
    )
    fetchSData(productsMapByGCode)
  }

  const attributesSelectorProps: AttributeSelectorProps[] = attributeNames?.map(
    (attributeName) => {
      const attributeSelectorProps: AttributeSelectorProps = {
        label: attributeSelectorMap[attributeName],
        name: attributeName,
        currentValue: currentAttributeSelectedValues[attributeName],
        attributeVariants: attributeNameOptions[attributeName]?.reduce(
          (acc: Array<AttributeSelectorItemProps>, option: string) => {
            const matchingAttSelectorMarkers =
              attSelectorMarkers?.length &&
              attSelectorMarkers?.find(
                (sm: AttributeSelectorMarkers) => sm?.value === option
              )?.label

            const currentPmiInternalProductCode = splitProductCode(
              currentProductVariant?.pmiInternalProductCode || ''
            )

            const attributeProductVariant = findProduct(
              currentPmiInternalProductCode,
              { ...currentAttributeSelectedValues, [attributeName]: option }
            )
            const shouldShowPrice = attributeSelector.some(
              (selector: any) =>
                selector.name === attributeName && selector.showPrice
            )

            const currentColorAndAttrVariant = variants?.filter(
              (variant: any) =>
                splitProductCode(variant.pmiInternalProductCode) ===
                  currentPmiInternalProductCode &&
                variant[attributeName] === option
            )?.[0]

            const currentVarinatFormattedPrice = formatPrice(
              currentColorAndAttrVariant?.price?.value,
              currentColorAndAttrVariant?.price?.priceCurrency,
              pageLocale
            )

            const attributeLabelOption =
              shouldShowPrice && currentVarinatFormattedPrice
                ? `${option}<br>${currentVarinatFormattedPrice}`
                : option

            const attributeSelectorMarkerLabel =
              shouldShowPrice && currentVarinatFormattedPrice
                ? `${matchingAttSelectorMarkers}<br>${currentVarinatFormattedPrice}`
                : matchingAttSelectorMarkers

            const attributeLabel = attSelectorMarkers?.length
              ? attributeSelectorMarkerLabel
              : attributeLabelOption

            const stockLevelStatus = currentProductStockMap.get(
              attributeProductVariant?.globalArticleCode
            )?.stock?.stockLevelStatus as unknown as keyof typeof stockStatusMap

            if (currentColorAndAttrVariant) {
              acc.push({
                value: option,
                attributeLabel,
                stock: stockStatusMap[stockLevelStatus]
              })
            }
            return acc
          },
          []
        ),
        onAttributeChange: (e, attributeOption) => {
          e?.preventDefault()
          const newProductAttributeSelectedValues = {
            ...currentAttributeSelectedValues,
            [attributeName]: attributeOption?.value
          }

          const clickedVariant = findProduct(
            splitProductCode(
              currentProductVariant?.pmiInternalProductCode || ''
            ),
            newProductAttributeSelectedValues
          )

          if (
            clickedVariant &&
            clickedVariant.globalArticleCode !== productID
          ) {
            setCurrentAttributeSelectedValues(newProductAttributeSelectedValues)
            setProductID(clickedVariant?.globalArticleCode)
            onVariantChange(clickedVariant)
          }
        }
      }
      return attributeSelectorProps
    }
  )

  const shouldShowAttributeSelector = attributesSelectorProps?.some(
    (attrProp: AttributeSelectorProps) =>
      Array.isArray(attrProp?.attributeVariants) &&
      attrProp?.attributeVariants?.length > 1
  )

  const colorName = currentProductVariant?.nuance?.description

  const [productQuantity, setProductQuantity] = useState(
    currentProductVariant?.minimumOrderQuantity
  )
  const currentProductVariantStockLevelStatus = currentProductStockMap.get(
    currentProductVariant.globalArticleCode
  )?.stock?.stockLevelStatus

  const dataLayerEcommerceProduct = createDataLayerEcommerceProduct({
    ...currentProductVariant,
    stock: {
      stockLevelStatus: currentProductVariantStockLevelStatus
    }
  })

  const addToCartMethodConfig = createAddToCartMethodConfig({
    orderType: general?.orderTypes,
    setIsButtonDisabled,
    setIsButtonLoading,
    dataLayerEcommerceProduct,
    productData: currentProductVariant,
    productID,
    caConfig: { ...caConfig, shopConfig: product, hybrisConfig: config },
    setActiveMiniCart,
    cookies,
    setCookie,
    cartLocalStorageJSON,
    setCartLocalStorage,
    setErroMessageContent,
    setDialogErrorMessageIsOpen,
    userData,
    productQuantity
  })

  const configMiniCart = useMiniCartConfig({
    productEntry,
    config: miniCart,
    variants: productData?.variants,
    cartLocalStorageJSON,
    activeMiniCart,
    setActiveMiniCart,
    productQuantityLabel: product?.quantityLabel
  })
  useEffect(() => {
    // Fallback product call on CSR when no server data is given
    if (!initialProductData) {
      const fetchPData = async () => {
        const pData = await fetchProductData(config, key)
        setProductData(pData)
      }
      fetchPData()
    }
  }, [])

  useEffect(() => {
    if (productData) {
      setIsButtonDisabled(false)
      setProductID(productData?.globalArticleCode)
      setVariants(getSortedVariants(visibleVariants))
      setBreadcrumbTitle(productData?.baseArticle?.baseModelName)
      ConfigureAndBuy.setupGcode = productData?.globalArticleCode
      updateGcodeSetupSubscribers()
      fetchSData(productsMapByGCode)
      const productVariant = {
        productFamily: productData?.productFamily,
        ...(productData.variants?.find(
          (v: any) => productData.globalArticleCode === v.globalArticleCode
        ) || {})
      }
      setProductQuantity(productVariant?.minimumOrderQuantity || 1)
    }
  }, [productData])

  useEffect(() => {
    if (canUseDom && Object.keys(currentProductVariant).length > 0) {
      pushDataLayerProductDetailsView(dataLayerEcommerceProduct)
    }
  }, [productID])

  let isDeliveryInTimeFrame = false
  let deliveryInfoMessage =
    deliveryMessage?.enableDeliveryInfo &&
    deliveryMessage?.defaultDeliveryMessage

  if (!isSsr && deliveryMessage?.enableDeliveryInfo) {
    if (!deliveryMessage?.timeFrames?.length) {
      isDeliveryInTimeFrame = true
    }

    deliveryMessage?.timeFrames?.forEach((timeFrame: any) => {
      const startTime = timeFrame.startTime?.split(':')
      const endTime = timeFrame.endTime?.split(':')
      const timeFrameStartDay = deliveryDays.indexOf(timeFrame.startDate) + 1
      const timeFrameEndDay = deliveryDays.indexOf(timeFrame.endDate) + 1

      const date = new Date()
      const day = date.getDay()

      // adapt sunday to be 7 instead of 0, to start week from Monday
      const modifiedSundayValue = day === 0 ? 7 : null
      const modifiedCurrentDay = modifiedSundayValue || day

      // check if start day is bigger that end day, and adapt values to it
      const endDay =
        timeFrameEndDay < timeFrameStartDay
          ? timeFrameEndDay + 7
          : timeFrameEndDay

      const currentDay =
        timeFrameEndDay < timeFrameStartDay
          ? modifiedCurrentDay + 7
          : modifiedCurrentDay

      // declare start date
      const startDate = new Date()
      startDate.setHours(Number(startTime[0]))
      startDate.setMinutes(Number(startTime[1]))

      // declare endDate
      const endDate = new Date()
      endDate.setHours(Number(endTime[0]))
      endDate.setMinutes(Number(endTime[1]))
      if (
        (currentDay > timeFrameStartDay && currentDay < endDay) ||
        (currentDay === timeFrameStartDay &&
          date.getTime() >= startDate.getTime()) ||
        (currentDay === endDay &&
          currentDay > timeFrameStartDay &&
          date.getTime() <= endDate.getTime()) ||
        (currentDay === endDay &&
          currentDay === timeFrameStartDay &&
          date.getTime() >= startDate.getTime() &&
          date.getTime() <= endDate.getTime())
      ) {
        isDeliveryInTimeFrame = true
        deliveryInfoMessage = timeFrame.deliveryMessage
      }
    })
  }

  const isGuestAddToCart =
    product?.guestAddToCartEnabled ||
    userData?.decoratedUserType !== UserTypes.Las

  const isPreorder = checkIsPreorder(currentProductVariant?.preOrderUntilDate)

  const isButtonInDefaultState =
    !isPreorder &&
    currentProductVariantStockLevelStatus &&
    currentProductVariantStockLevelStatus !== outOfStockValue &&
    isGuestAddToCart

  const isQTYSelectorVisible =
    isButtonInDefaultState && general?.enableQuantitySelector

  const colorSelectorVariants = useMemo(
    () =>
      variants?.reduce((acc: any[], variant: any) => {
        const variantPmiInternalProductCode = splitProductCode(
          variant?.pmiInternalProductCode
        )
        if (
          variantPmiInternalProductCode &&
          !acc.some(
            (colorVariant) =>
              variantPmiInternalProductCode ===
              splitProductCode(colorVariant.pmiInternalProductCode)
          )
        ) {
          const colorProductVariant = findProduct(
            variantPmiInternalProductCode,
            currentAttributeSelectedValues
          )

          const colorProductVariantStockLevelStatus =
            currentProductStockMap.get(colorProductVariant?.globalArticleCode)
              ?.stock
              ?.stockLevelStatus as unknown as keyof typeof stockStatusMap

          acc.push({
            pmiInternalProductCode: variantPmiInternalProductCode,
            colorLabel: variant?.nuance?.description,
            value: variantPmiInternalProductCode,
            linkUrl: productRootPath
              ? productRootPath?.replace(
                  '$SEO_URL_PLACEHOLDER',
                  variant?.seo?.productsSeoUrl || ''
                )
              : variant?.seo?.productsSeoUrl || '',
            colorHex: variant?.nuance?.hexCode,
            stock: stockStatusMap[colorProductVariantStockLevelStatus],
            'aria-label':
              colorProductVariantStockLevelStatus === outOfStockValue
                ? `${variant?.nuance?.description}-out-of-stock`
                : variant?.nuance?.description
          })
        }

        return acc
      }, []) || [],
    [variants, currentAttributeSelectedValues, currentProductStockMap]
  )

  // handle change of color selector
  const onColorClick = (e: any, clickedColor: any) => {
    e?.preventDefault()
    const clickedVariant = findProduct(
      clickedColor.value,
      currentAttributeSelectedValues
    )

    if (clickedVariant && clickedVariant.globalArticleCode !== productID) {
      setProductID(clickedVariant.globalArticleCode)
      onVariantChange(clickedVariant)
    }
  }

  const stockInfoLabel = getStockInfoLabel({
    isSsr,
    stockLevelStatus: currentProductVariantStockLevelStatus,
    product
  })

  const addToCartButtonLabel = getAddToCartButtonLabel({
    guestAddToCartEnabled: product?.guestAddToCartEnabled,
    preOrderUntilDate: currentProductVariant?.preOrderUntilDate,
    stockLevelStatus: currentProductVariantStockLevelStatus,
    addToCartLabel: product?.addToCartLabel,
    preOrderLabel: product?.preOrderLabel,
    notifyMe: notifyMe?.enabled,
    notifyMeCTALabel: notifyMe?.label,
    outOfStockLabel: product?.outOfStockCTALabel,
    loginRequiredCTALabel: product?.loginRequiredCTALabel,
    userData
  })

  const formattedOriginalPrice = currentProductVariant?.price?.value
    ? formatPrice(
        currentProductVariant?.price?.value,
        currentProductVariant?.price?.priceCurrency,
        pageLocale
      )
    : null

  const formattedDiscountPrice = currentProductVariant?.price?.priceDiscount
    ?.value
    ? formatPrice(
        Number(currentProductVariant?.price?.priceDiscount?.value),
        currentProductVariant?.price?.priceCurrency,
        pageLocale
      )
    : null

  const originalPriceMessage = formattedOriginalPrice
    ? general?.currentPriceLabel?.replace(
        CURRENT_PRICE_PLACEHOLDER,
        formattedOriginalPrice
      ) || formattedOriginalPrice
    : null

  const discountPriceMessage = formattedDiscountPrice
    ? general?.oldPriceLabel?.replace(
        OLD_PRICE_PLACEHOLDER,
        formattedDiscountPrice
      ) || formattedDiscountPrice
    : null

  const hasDiscount =
    currentProductVariant?.price?.value &&
    currentProductVariant?.price?.priceDiscount?.value &&
    currentProductVariant?.price?.value >=
      Number(currentProductVariant?.price?.priceDiscount?.value)

  const orderQTYValues = getOrderQuantityValues(
    currentProductVariant?.minimumOrderQuantity,
    currentProductVariant?.maximumOrderQuantity
  )

  const productDescription =
    currentProductVariant?.productLongDescription ||
    currentProductVariant?.productShortDescription ||
    ''

  const showFlavourProfiles =
    flavour?.flavourBanEnabled &&
    general?.enableFlavourProfile &&
    (currentProductVariant?.intensity ||
      currentProductVariant?.intensityMeter ||
      flavour?.intensityLabel ||
      currentProductVariant?.intensityInfo)

  const productImageGallery =
    currentProductVariant && currentProductVariant?.assets
      ? currentProductVariant?.assets?.map((asset: any, index: number) => {
          const src = getOptimizedImageExtension(asset?.assetFullUrl)
          const srcSetData = IMAGE_GALLERY_BREAKPOINTS.map(
            ({ width, minWidth }) => `${src}?width=${width} ${minWidth}w`
          ).join(', ')
          const srcSetDataWithDefault = `${srcSetData}, ${src}?width=${DEFAULT_IMAGE_WIDTH}`

          return {
            ...(asset ? { src } : {}),
            width: '100%',
            renditions: true,
            srcSetData: srcSetDataWithDefault,
            srcThumbs: `${src}?width=64`,
            srcFullScreen: `${src}?width=${DEFAULT_IMAGE_WIDTH}`,
            lazyLoading: index === 0 ? general?.enableLazyLoad : true
          }
        })
      : [
          {
            src: general?.backupImageUrl,
            width: '100%'
          }
        ]

  const showStockInfoLabel =
    (product?.showInStockInfoEnable &&
      !isSsr &&
      stockInfoLabel === product?.inStockLabel) ||
    (product?.showInStockInfoEnable && isSsr) ||
    (stockInfoLabel !== product?.inStockLabel && !isSsr)
  const shouldShowAddToCart = addToCart?.addToCartCtaVisible

  const onATCButtonClick = () => {
    setMarkerProductData(productData)
    if (addToCartButtonLabel === notifyMe?.label) {
      setDialogNotifyMeIsOpen(true)
    }
    if (addToCartButtonLabel === product?.addToCartLabel) {
      handleAddToCart({
        ...addToCartMethodConfig
      })
    }
  }

  const addToCartProperties = {
    onButtonClick: onATCButtonClick,
    buttonLabel: addToCartButtonLabel,
    loadingButton: isButtonLoading,
    disabledButton:
      addToCartButtonLabel === product?.outOfStockCTALabel || isButtonDisabled,
    qtySelectorProps: {
      ...(isQTYSelectorVisible &&
      addToCartButtonLabel === product?.addToCartLabel
        ? { orderQTY: orderQTYValues }
        : {}),
      defaultQTYValue: productQuantity?.toString(),
      'aria-label': quantityLabel
    },
    onQTYChange: (event: any) => {
      setProductQuantity(Number(event.target.value))
    }
  }

  const addToCartAlternativeContent = addToCart?.xfAddToCartContent
    ? {
        model: {
          xfPath: `${addToCart?.xfForAddToCart}`,
          xfContent: { ...addToCart?.xfAddToCartContent }
        }
      }
    : null

  const uspXFContent = general?.productUSPs
    ? {
        model: {
          xfPath: `${general?.productUSPs}`,
          xfContent: { ...general?.productUSPsXFContent }
        }
      }
    : null

  const configureAndBuyProps = {
    ...(currentProductVariant?.badges?.length
      ? {
          productChips: {
            chipPropsList: currentProductVariant?.badges?.map((badge: any) => ({
              label: badge.backgroundColor?.name,
              color: badge.fontColor?.hexCode,
              backgroundColor: badge.backgroundColor?.hexCode
            }))
          }
        }
      : {}),
    ...(currentProductVariant?.globalArticleName
      ? {
          productHeader: {
            productName: currentProductVariant?.globalArticleName,
            ...(currentProductVariant?.nuance?.name
              ? {
                  description: currentProductVariant?.nuance?.name
                }
              : {})
          }
        }
      : {}),
    ...(colorSelectorVariants?.length
      ? {
          colorSelector: {
            id: COLOR_SELECTOR_CONTAINER_ID,
            currentValue: currentColorCode,
            name: colorName,
            showText: true,
            onChange: onColorClick,
            type: ColorSelectorTypes.Hyperlink,
            'aria-label': product?.colorGroupAriaLabel,
            ...(colorSelectorVariants?.length
              ? { colorVariants: colorSelectorVariants }
              : {})
          }
        }
      : {}),
    ...(showStockInfoLabel
      ? {
          stockInfo: {
            stockLabel: stockInfoLabel,
            // @ts-expect-error Type 'undefined' cannot be used as an index type.ts(2538)
            ...(stockStatusMap[currentProductVariantStockLevelStatus]
              ? {
                  stockStatus:
                    !isSsr &&
                    // @ts-expect-error Type 'undefined' cannot be used as an index type.ts(2538)
                    stockStatusMap[currentProductVariantStockLevelStatus]
                }
              : {}),
            showStockMessage: showStockInfoLabel
          }
        }
      : {}),
    ...(showFlavourProfiles
      ? {
          flavourProfiles: {
            profiles: getIntensityGauge(
              currentProductVariant,
              flavour?.intensityLabel
            )
          } as FlavourProfilesProps
        }
      : {}),
    ...(attributesSelectorProps?.length && shouldShowAttributeSelector
      ? { attributeSelectors: attributesSelectorProps }
      : {}),
    ...(shouldShowAddToCart
      ? {
          addToCart: <AddToCart {...addToCartProperties} />
        }
      : null),
    ...(addToCartAlternativeContent
      ? {
          addToCartAlternativeContent: (
            <ExperienceFragmentContent {...addToCartAlternativeContent} />
          )
        }
      : {}),
    ...(ratesAndReviews && ratesAndReviews.enabled
      ? {
          rating: {
            productId: key
          }
        }
      : {}),
    ...(shouldShowAddToCart
      ? {
          stickyCTA: {
            id: mobileStickyCtaId,
            ...(currentProductVariant?.globalArticleName
              ? {
                  productName: currentProductVariant?.globalArticleName
                }
              : {}),
            onButtonClick: onATCButtonClick,
            loadingButton: isButtonLoading,
            disabledButton:
              addToCartButtonLabel === product?.outOfStockCTALabel ||
              isButtonDisabled,
            buttonLabel: addToCartButtonLabel,
            colorName,
            onColorNameClick: () => {
              const targetEl = document.getElementById(
                COLOR_SELECTOR_CONTAINER_ID
              )
              const scrollPosition =
                (targetEl?.getBoundingClientRect().top || 0) +
                window.scrollY -
                topShift
              window.scrollTo({
                top: scrollPosition,
                behavior: 'smooth'
              })
            },
            ...(originalPriceMessage
              ? {
                  ...(hasDiscount && discountPriceMessage
                    ? {
                        currentPrice: discountPriceMessage,
                        originalPrice: originalPriceMessage
                      }
                    : { currentPrice: originalPriceMessage })
                }
              : {}),
            // @ts-expect-error Type 'undefined' cannot be used as an index type.ts(2538)
            ...(stockStatusMap[currentProductVariantStockLevelStatus]
              ? {
                  productStatus:
                    // @ts-expect-error Type 'undefined' cannot be used as an index type.ts(2538)
                    stockStatusMap[currentProductVariantStockLevelStatus]
                }
              : {}),
            ...(product?.stickyAddToCartHidingObservedIDs?.length
              ? {
                  observableIDs: product?.stickyAddToCartHidingObservedIDs
                }
              : {}),
            onStickyDisplayChange: (isStickyVisible: boolean) => {
              setLayoutBottomShift(
                isStickyVisible && isMobile
                  ? document.getElementById(mobileStickyCtaId)?.offsetHeight ||
                      0
                  : 0
              )
            }
          }
        }
      : {}),
    ...(deliveryMessage?.enableDeliveryInfo
      ? {
          deliveryInfo: {
            ...(deliveryMessage?.deliveryImageUrl
              ? {
                  image: {
                    src: deliveryMessage?.deliveryImageUrl,
                    height: '48',
                    width: '48'
                  }
                }
              : {}),
            ...(deliveryInfoMessage
              ? {
                  content: deliveryInfoMessage
                }
              : {})
          }
        }
      : {}),
    backToShop: {
      label: general?.backToPLPLabel,
      linkUrl: general?.backToPLPUrl
    },
    ...(uspXFContent
      ? { highlightsContent: <ExperienceFragmentContent {...uspXFContent} /> }
      : {}),
    ...(general?.background ? { background: general?.background } : {}),
    ...(productDescription
      ? {
          productInfo: {
            description: productDescription
          }
        }
      : {}),
    ...(productImageGallery?.length
      ? {
          productImageGallery: {
            gallery: productImageGallery,
            zoomLabel: imageGallery?.doubleClickToZoomLabel,
            accessibilityProps: {
              dialogCloseButtonAriaLabel:
                imageGallery?.closeFullScreenAriaLabel,
              videoPosterPlayButtonAriaLabel: imageGallery?.playButtonAriaLabel,
              zoomButtonAriaLabel: imageGallery?.openFullScreenAriaLabel,
              sliderAccessibilityProps: {
                nextSlideMessage: imageGallery?.nextSlideAriaLabel,
                prevSlideMessage: imageGallery?.previousSlideAriaLabel,
                lastSlideMessage: imageGallery?.lastSlideAriaLabel,
                firstSlideMessage: imageGallery?.firstSlideAriaLabel
              }
            }
          }
        }
      : {}),
    ...(originalPriceMessage
      ? {
          priceInfo: {
            ...(hasDiscount && discountPriceMessage
              ? {
                  currentPriceTextProps: {
                    content: discountPriceMessage
                  },
                  originalPriceTextProps: {
                    content: originalPriceMessage
                  }
                }
              : {
                  currentPriceTextProps: {
                    content: originalPriceMessage
                  }
                }),
            ...(currentProductVariant?.price?.priceDiscount
              ?.discountValueDescription
              ? {
                  message:
                    currentProductVariant?.price?.priceDiscount
                      ?.discountValueDescription
                }
              : {})
          }
        }
      : {})
  }

  const configCartErrorMessageDialog = {
    errorMessageContent,
    dialogErrorMessageIsOpen,
    setDialogErrorMessageIsOpen
  }

  const configNotifyMeDialog = {
    isOpen: dialogNotifyMeIsOpen,
    onClose: () => setDialogNotifyMeIsOpen(false),
    zIndex: 1351,
    title: notifyMe?.dialogTitle,
    maxWidth: DialogMaxWidthSizes.SM,
    fullWidth: true,
    dialogContentPadding: true,
    closeButtonStyle: IconButtonStyles.Default,
    dismissible: true,
    closeAriaLabel: notifyMe?.closeAriaLabel
  }

  const configNotifyMe = {
    model: {
      ...notifyMe?.xfContent,
      cqItems: notifyMe?.xfContent?.[':items'],
      cqItemsOrder: notifyMe?.xfContent?.[':itemsOrder'],
      cqType: notifyMe?.xfContent?.[':type']
    }
  }

  return (
    currentProductVariant && (
      <EditableComponent {...editableProps}>
        <CAndB {...configureAndBuyProps} />
        <MiniCart {...configMiniCart} />
        <CartErrorMessageDialog {...configCartErrorMessageDialog} />
        <UIFoundationsDialog {...configNotifyMeDialog}>
          <Container model={{ ...configNotifyMe } as ModelProps} />
        </UIFoundationsDialog>
      </EditableComponent>
    )
  )
})

// left static function for emulating getServerProps
// @ts-expect-error Property 'getServerSideProps' does not exist
ConfigureAndBuy.getServerSideProps = async ({ ...props }) => {
  const { key, config /* general */ } = props
  const fetchedData = await fetchProductData(config, key)

  /*
  const productHighlightsContent = await fetchModel({
    cqPath: general?.productUSPs
  })
  */

  return {
    ...props,
    initialProductData: { ...fetchedData }
    /*
    ...(productHighlightsContent && Object.keys(productHighlightsContent).length
      ? { highlightComponent: { ...productHighlightsContent } }
      : null)
    */
  }
}

// @ts-expect-error Property 'getHeadData' does not exist
ConfigureAndBuy.getHeadData = async ({
  ...props
}): Promise<GetHeadDataReturnType> => {
  const {
    initialProductData,
    general: { disableMainImagePreload } = {},
    pageUrl
  } = props || {}

  return {
    status: initialProductData?.errors?.[0]?.status || 200,
    seoTitle: initialProductData?.seo?.productsSeoTitle,
    dynamicPageTitle: initialProductData?.globalArticleName,
    seoDescription: initialProductData?.seo?.productsSeoDescription,
    assetFullUrl: getOptimizedImageExtension(
      initialProductData?.assets?.[0]?.assetFullUrl
    ),
    headTags: disableMainImagePreload ? [] : headTagsConfig(initialProductData),
    jsonLD: {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      sku: initialProductData?.globalArticleCode,
      '@id': pageUrl,
      name: initialProductData?.globalArticleName,
      offers: {
        '@type': 'Offer',
        url: pageUrl,
        priceCurrency: initialProductData?.price?.priceCurrency,
        price: initialProductData?.price?.value,
        availability: 'https://schema.org/InStock'
      },
      description: initialProductData?.productLongDescription,
      brand: {
        '@type': 'Thing',
        name: 'IQOS'
      },
      image: getOptimizedImageExtension(
        initialProductData?.assets?.[0]?.assetFullUrl
      ),
      gtin: initialProductData?.gtin
    }
  }
}

ConfigureAndBuy.setupGcode = null

ConfigureAndBuy.gcodeSetupSubscribers = {}

ConfigureAndBuy.subscribeToGcodeSetup = (
  id: string,
  action: (gcode: string) => void
) => {
  if (id) {
    ConfigureAndBuy.unsubscribeFromGcodeSetup(id)
    ConfigureAndBuy.gcodeSetupSubscribers[id] = { action }
    if (ConfigureAndBuy.setupGcode) {
      action(ConfigureAndBuy.setupGcode)
    }
  }
}

ConfigureAndBuy.unsubscribeFromGcodeSetup = (id: string) => {
  if (id && ConfigureAndBuy.gcodeSetupSubscribers[id]) {
    delete ConfigureAndBuy.gcodeSetupSubscribers[id]
  }
}

export default ConfigureAndBuy
